import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiMoreVertical,
  FiRefreshCw,
  FiSearch,
  FiX,
} from "react-icons/fi";
import sellerApi from "../../utils/sellerApi";
import { showError, showSuccess } from "../../utils/Toast";
import Modal from "../Modal/Modal";
import AddEditProductModal from "../Modal/AddEditProductModal";
import "../../Styles/Seller/Product.css";
import {
  DEFAULT_PRODUCT_IMAGE,
  withDefaultProductImage,
} from "../../utils/productPresentation";
import dhiramsymbol from "../../assests/dhirams-png.webp";
import { useSellerAuth } from "../../Common/SellerAuthContext";

const STATUS_META = {
  active: { label: "Active", className: "active" },
  pending: { label: "Pending", className: "pending" },
  rejected: { label: "Rejected", className: "rejected" },
  inactive: { label: "Inactive", className: "inactive" },
  out_of_stock: { label: "Out of Stock", className: "inactive" },
};

function stockNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getDisplayProductStock(product) {
  return stockNumber(
    product?.stock_quantity ?? product?.product_stock_quantity ?? 0,
  );
}

function getSellerAvailableStock(product) {
  return stockNumber(
    product?.seller_available_quantity ??
      product?.seller_stock_quantity ??
      product?.available_quantity ??
      0,
  );
}

function formatPriceLabel(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00";
}

function getSellerProductPrice(product) {
  const candidates = [
    product?.selling_price,
    product?.seller_price,
    product?.price,
    product?.product_price,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return 0;
}

function getSizeUnitLabel(product) {
  return [product?.size_dosage, product?.unit].filter(Boolean).join(" • ");
}

const PRODUCTS_PER_PAGE = 10;

export default function StockManagement() {
  const { isSellerAuthenticated } = useSellerAuth();
  const isSellerUser = isSellerAuthenticated;
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PRODUCTS_PER_PAGE,
    total: 0,
    totalPages: 1,
  });
  const [summary, setSummary] = useState({
    totalProducts: 0,
    active: 0,
    pending: 0,
    rejected: 0,
    inactive: 0,
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockValue, setStockValue] = useState("");
  const [statusValue, setStatusValue] = useState("active");
  const [stockError, setStockError] = useState("");
  const [saving, setSaving] = useState(false);
  const [rejectReasonProduct, setRejectReasonProduct] = useState(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestModalInitialData, setRequestModalInitialData] = useState(null);
  const [requestDataLoading, setRequestDataLoading] = useState(false);
  const [categoryData, setCategoryData] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [brandData, setBrandData] = useState([]);
  const [subcategoryData, setSubcategoryData] = useState([]);
  const [requestDataLoaded, setRequestDataLoaded] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const actionMenuRef = useRef(null);

  const loadProducts = useCallback(
    async (pageNumber = currentPage) => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: String(pageNumber),
          limit: String(PRODUCTS_PER_PAGE),
        });

        if (search.trim()) params.set("search", search.trim());
        if (brandFilter) params.set("brand", brandFilter);
        if (categoryFilter) params.set("category", categoryFilter);
        if (statusFilter) params.set("status", statusFilter);

        const response = await sellerApi.get(
          `seller/stocks/products?${params.toString()}`,
        );
        const nextProducts = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const nextPagination = response.data?.pagination || {};
        const nextSummary = response.data?.summary || {};
        const resolvedPage = Number(nextPagination.page || pageNumber || 1);

        setProducts(nextProducts);
        setPagination({
          page: resolvedPage,
          limit: Number(nextPagination.limit || PRODUCTS_PER_PAGE),
          total: Number(nextPagination.total || nextProducts.length),
          totalPages: Number(nextPagination.totalPages || 1),
        });
        if (resolvedPage !== currentPage) {
          setCurrentPage(resolvedPage);
        }
        setSummary({
          totalProducts: Number(
            nextSummary.totalProducts ||
              nextPagination.total ||
              nextProducts.length,
          ),
          active: Number(nextSummary.active || 0),
          pending: Number(nextSummary.pending || 0),
          rejected: Number(nextSummary.rejected || 0),
          inactive: Number(nextSummary.inactive || 0),
        });
      } catch (error) {
        showError(
          error.response?.data?.message || "Failed to load stock products",
        );
      } finally {
        setLoading(false);
      }
    },
    [brandFilter, categoryFilter, currentPage, search, statusFilter],
  );

  const loadFilterOptions = async () => {
    try {
      const [categoryResponse, brandResponse] = await Promise.all([
        sellerApi.get("getCategories"),
        sellerApi.get("getAllBrands"),
      ]);
      setCategoryData(categoryResponse.data?.data || []);
      setBrandData(brandResponse.data?.data || []);
    } catch (error) {
      showError(
        error.response?.data?.message || "Failed to load filter options",
      );
    }
  };

  const formatDateLabel = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadProducts(currentPage);
  }, [currentPage, loadProducts]);

  useEffect(() => {
    if (!actionMenu) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (actionMenuRef.current?.contains(target)) {
        return;
      }
      if (target?.closest?.("[data-action-menu-trigger='true']")) {
        return;
      }
      setActionMenu(null);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setActionMenu(null);
      }
    };

    const handleViewportChange = () => {
      setActionMenu(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [actionMenu]);

  const loadRequestDependencies = async () => {
    if (requestDataLoaded) return true;

    try {
      setRequestDataLoading(true);
      const [
        categoryResponse,
        companyResponse,
        brandResponse,
        subcategoryResponse,
      ] = await Promise.all([
        sellerApi.get("getCategories"),
        sellerApi.get("getCompanies"),
        sellerApi.get("getAllBrands"),
        sellerApi.get("getSubCategories"),
      ]);

      setCategoryData(categoryResponse.data?.data || []);
      setCompanyData(companyResponse.data?.data || []);
      setBrandData(brandResponse.data?.data || []);
      setSubcategoryData(subcategoryResponse.data?.data || []);
      setRequestDataLoaded(true);
      return true;
    } catch (error) {
      showError(
        error.response?.data?.message || "Failed to load product request data",
      );
      return false;
    } finally {
      setRequestDataLoading(false);
    }
  };

  const openRequestModal = async () => {
    const ready = await loadRequestDependencies();
    if (ready) {
      setRequestModalInitialData(null);
      setRequestModalOpen(true);
    }
  };

  const openEditRequestModal = async (product) => {
    const ready = await loadRequestDependencies();
    if (!ready || !product?.can_edit_request) return;

    setRequestModalInitialData({
      id: product.id,
      name: product.name,
      title: product.name,
      categoryid: product.category_id || "",
      companyid: product.company_id || "",
      brandid: product.brand_id || "",
      description: product.description || "",
      price: product.price || "",
      discount: product.discount || 0,
      inventory:
        product.product_stock_quantity ?? product.seller_stock_quantity ?? 0,
      status: product.product_status || "inactive",
      features: product.featured || [],
      attributes: product.attributes || [],
      subcategoryid: product.sub_category_id || "",
      images: Array.isArray(product.image_urls) ? product.image_urls : [],
    });
    setRequestModalOpen(true);
  };

  const openUpdateModal = (product) => {
    if (!product?.is_editable) return;
    setSelectedProduct(product);
    setStockValue("");
    setStatusValue(
      product.seller_stock_status === "inactive" ? "inactive" : "active",
    );
    setStockError("");
  };

  const closeUpdateModal = () => {
    if (saving) return;
    setSelectedProduct(null);
    setStockValue("");
    setStatusValue("active");
    setStockError("");
  };

  const saveStock = async (event) => {
    event.preventDefault();
    if (!selectedProduct) return;
    const addedQty = Number(stockValue);
    if (!Number.isFinite(addedQty) || addedQty <= 0) {
      setStockError("Please enter a valid stock quantity to add");
      showError("Please enter a valid stock quantity to add");
      return;
    }
    setStockError("");

    try {
      setSaving(true);
      if (selectedProduct.stock_id) {
        await sellerApi.put(`seller/stocks/${selectedProduct.stock_id}`, {
          added_quantity: addedQty,
          status: statusValue,
        });
      } else {
        await sellerApi.post("seller/stocks", {
          product_id: selectedProduct.id,
          added_quantity: addedQty,
          status: statusValue,
        });
      }
      showSuccess("Stock added successfully");
      await loadProducts(currentPage);
      setSelectedProduct(null);
      setStockValue("");
      setStatusValue("active");
    } catch (error) {
      showError(error.response?.data?.message || "Failed to add stock");
    } finally {
      setSaving(false);
    }
  };

  const closeRequestModal = () => {
    setRequestModalOpen(false);
    setRequestModalInitialData(null);
  };

  const closeRejectReasonModal = () => {
    setRejectReasonProduct(null);
  };

  const handleRequestSaved = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    loadProducts(1);
  };

  const toggleActionMenu = (event, productId) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();

    setActionMenu((currentMenu) =>
      currentMenu?.productId === productId
        ? null
        : {
            productId,
            top: rect.bottom + 6,
            left: rect.right,
          },
    );
  };

  return (
    <div className="mbp-product-page seller-stock-page">
      <div className="mbp-product-header">
        <div>
          <h1>Seller Products</h1>
          <p className="mbp-subtitle">
            Track approval status, visibility, and stock for your seller
            products.
          </p>
        </div>
        <div className="mbp-product-actions">
          {isSellerUser && (
            <button
              type="button"
              className="mbp-primary-btn"
              onClick={openRequestModal}
              disabled={requestDataLoading}
            >
              {requestDataLoading ? "Preparing..." : "Request Product"}
            </button>
          )}
          <button
            type="button"
            className="mbp-icon-btn"
            onClick={() => loadProducts(currentPage)}
            disabled={loading}
          >
            <FiRefreshCw /> <span>{loading ? "Loading" : "Refresh"}</span>
          </button>
        </div>
      </div>

      <div className="mbp-stats-grid">
        <div className="mbp-stat-card">
          <div className="mbp-stat-content">
            <h3>{summary.totalProducts}</h3>
            <p>Products</p>
          </div>
        </div>
        <div className="mbp-stat-card">
          <div className="mbp-stat-content">
            <h3>{summary.active}</h3>
            <p>Active</p>
          </div>
        </div>
        <div className="mbp-stat-card">
          <div className="mbp-stat-content">
            <h3>{summary.pending}</h3>
            <p>Pending</p>
          </div>
        </div>
        <div className="mbp-stat-card">
          <div className="mbp-stat-content">
            <h3>{summary.rejected}</h3>
            <p>Rejected</p>
          </div>
        </div>
      </div>

      <div className="mbp-product-card">
        <div className="mbp-product-filters">
          <div className="mbp-search-wrap">
            <FiSearch className="mbp-search-icon" />
            <input
              type="text"
              placeholder="Search products, brands, or categories..."
              className="mbp-search-input"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setCurrentPage(1);
              }}
            />
            {search && (
              <button
                type="button"
                className="mbp-clear-search"
                onClick={() => {
                  setSearch("");
                  setCurrentPage(1);
                }}
              >
                <FiX />
              </button>
            )}
          </div>
          <div className="mbp-right-filters">
            <select
              className="mbp-select-box"
              value={brandFilter}
              onChange={(event) => {
                setBrandFilter(event.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All brands</option>
              {brandData.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
            <select
              className="mbp-select-box"
              value={categoryFilter}
              onChange={(event) => {
                setCategoryFilter(event.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All categories</option>
              {categoryData.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <select
              className="mbp-select-box"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
              <option value="inactive">Inactive</option>
            </select>
            <button
              type="button"
              className="mbp-icon-btn"
              onClick={() => {
                setSearch("");
                setBrandFilter("");
                setCategoryFilter("");
                setStatusFilter("");
                setCurrentPage(1);
              }}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="mbp-table-wrap">
          <div className="mbp-table">
            <div className="mbp-table-header">
              <div className="mbp-col mbp-col-image">Image</div>
              <div className="mbp-col mbp-col-name">Product</div>
              <div className="mbp-col mbp-col-name">Size</div>
              <div className="mbp-col">Brand</div>
              <div className="mbp-col">Category</div>
              <div className="mbp-col">Sub-category</div>
              <div className="mbp-col">Price</div>
              {/* <div className="mbp-col mbp-col-stock">Current Stock</div> */}
              {/* <div className="mbp-col">Reserved</div> */}
              <div className="mbp-col">Product Stock</div>
              {/* <div className="mbp-col">Seller Available</div> */}
              <div className="mbp-col">Status</div>
              <div className="mbp-col mbp-col-actions">Action</div>
            </div>

            {products.map((product) => (
              <div className="mbp-table-row" key={product.id}>
                <div className="mbp-col mbp-col-image">
                  <div className="mbp-product-image">
                    <img
                      src={withDefaultProductImage(product.image_url)}
                      alt={product.name}
                      onError={(event) => {
                        event.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                      }}
                    />
                  </div>
                </div>
                <div className="mbp-col mbp-col-name">
                  <strong>{product.name}</strong>
                  {/* <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                    {getSizeUnitLabel(product) || "Size / unit not available"}
                  </div> */}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginTop: "4px",
                  }}
                >
                  {getSizeUnitLabel(product) || "Size / unit not available"}
                </div>
                <div className="mbp-col">{product.brand_name || "-"}</div>
                <div className="mbp-col">{product.category_name || "-"}</div>
                <div className="mbp-col">
                  {product.sub_category_name || "-"}
                </div>
                <div className="mbp-col">
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <img
                      src={dhiramsymbol}
                      alt="AED"
                      style={{
                        width: "16px",
                        height: "16px",
                        objectFit: "contain",
                      }}
                    />
                    <span>
                      {formatPriceLabel(getSellerProductPrice(product))}
                    </span>
                  </span>
                </div>
                {/* <div className="mbp-col mbp-col-stock">
                    {stockNumber(product.seller_stock_quantity)} units
                  </div> */}
                {/* <div className="mbp-col">{stockNumber(product.reserved_quantity)}</div> */}
                <div className="mbp-col">{getDisplayProductStock(product)}</div>
                {/* <div className="mbp-col">{getSellerAvailableStock(product)}</div>I have an existing e-commerce/medical store application integrated with Zoho Inventory.

Project paths:

Backend:
E:\New-elimia-production\backend

Frontend User/Seller:
E:\New-elimia-production\frontend-user

Current Zoho item stock display:

Opening Stock: 16
Stock on Hand: 16
Committed Stock: 0
Available for Sale: 16

Requirement:
When seller adds stock from seller panel, do not update Zoho Opening Stock.

Correct Zoho behavior:
Opening Stock should remain the original starting stock.
Stock on Hand should increase.
Available for Sale should increase automatically if Committed Stock is 0.

Example:
Current:
Opening Stock = 16
Stock on Hand = 16
Committed Stock = 0
Available for Sale = 16

Seller adds +1.

Expected:
Opening Stock = 16
Stock on Hand = 17
Committed Stock = 0
Available for Sale = 17

VERY IMPORTANT:
Do not change anything apart from seller stock addition Zoho Inventory adjustment.
Do not edit Zoho opening stock.
Do not re-import opening stock.
Do not overwrite item master stock.
Do not change order/shipment flow.
Do not change Zoho invoice logic.
Do not change product create/update logic.
Do not change frontend UI except if needed to show correct message.

Backend requirement:
When seller adds stock, update website DB stock:

products.stock_quantity = products.stock_quantity + added_quantity

Then update Zoho Inventory using Inventory Adjustment with positive quantity:

quantity_adjusted = +added_quantity

Reason:
Seller Stock Addition

Reference:
seller-stock-add-{sellerId}-{productId}-{timestamp}

Do not use Opening Stock API/import for seller stock addition.
Do not map Opening Stock again.
Do not edit opening_stock field.
Do not call item update to change opening stock.

Zoho expected result:
Inventory Adjustment +1 should increase:

* Stock on Hand
* Available for Sale, if committed stock is 0

Committed Stock should only change when there are sales orders/commitments.
Opening Stock should stay same.

Search backend:

* seller stock update
* stock_quantity
* zohoInventoryService
* adjustZohoStock
* inventoryadjustments
* opening_stock
* opening stock
* item update
* update item
* zoho_item_id

Fix:
If code currently updates Zoho item opening stock, remove that from seller stock update flow.

Use only inventory adjustment:

await zohoInventoryService.adjustStock({
zohoItemId: product.zoho_item_id,
quantityAdjusted: addedQty,
reason: "Seller Stock Addition",
referenceNumber: `seller-stock-add-${sellerId}-${productId}-${Date.now()}`
});

Important:
addedQty must be positive.
If seller adds 1, Zoho adjustment must be +1.
If shipment reduces 1, Zoho adjustment must be -1.
Do not mix these.

Testing required:

Test 1:
Before seller add:
Opening Stock = 16
Stock on Hand = 16
Available for Sale = 16

Seller adds +1.

Expected:
Opening Stock = 16
Stock on Hand = 17
Available for Sale = 17

Test 2:
Seller adds +3.

Expected:
Opening Stock unchanged.
Stock on Hand increases by 3.
Available for Sale increases by 3 if committed stock is 0.

Test 3:
If committed stock exists:
Stock on Hand increases by added quantity.
Available for Sale should follow Zoho calculation:
Stock on Hand - Committed Stock.

Test 4:
Shipment stock reduction:
Admin creates shipment for quantity 1.

Expected:
Zoho adjustment -1.
Stock on Hand reduces by 1.
Available for Sale reduces accordingly.
Opening Stock still unchanged.

Strict rules:
Do not update Opening Stock after item creation.
Do not use import for seller stock addition.
Do not change Zoho item type/product type.
Do not change unrelated Zoho logic.
Do not change order/shipment/payment/review/barcode logic.
Only use inventory adjustment for stock movement.

Final output required:

1. Confirm seller stock addition uses Zoho Inventory Adjustment.
2. Confirm Opening Stock is not modified.
3. Test result: +1 stock makes Stock on Hand 17.
4. Test result: Available for Sale also becomes 17 when committed stock is 0.
5. Confirmation no unrelated logic was changed.
 */}
                <div className="mbp-col">
                  <span
                    className={`mbp-status ${STATUS_META[product.display_status]?.className || "inactive"}`}
                  >
                    {STATUS_META[product.display_status]?.label || "Inactive"}
                  </span>
                </div>
                <div className="mbp-col mbp-col-actions">
                  {product.is_editable ||
                  product.can_edit_request ||
                  product.display_status === "rejected" ? (
                    <div className="mbp-action-menu-wrap">
                      <button
                        type="button"
                        className="mbp-action-btn"
                        data-action-menu-trigger="true"
                        onClick={(event) => toggleActionMenu(event, product.id)}
                        title="Product actions"
                      >
                        <FiMoreVertical />
                      </button>
                    </div>
                  ) : (
                    <span className="mbp-action-disabled">-</span>
                  )}
                </div>
              </div>
            ))}

            {!loading && products.length === 0 && (
              <div className="mbp-empty-state">
                <h3>No products found</h3>
                <p>Your seller products will appear here when available.</p>
              </div>
            )}
          </div>
        </div>

        {pagination.total > 0 && (
          <div className="mbp-pagination">
            <div className="mbp-pagination-info">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
              {(pagination.page - 1) * pagination.limit + products.length} of{" "}
              {pagination.total} products
            </div>
            <div className="mbp-pagination-controls">
              <button
                type="button"
                className="mbp-pagination-btn"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={pagination.page <= 1 || loading}
              >
                <FiChevronLeft /> Previous
              </button>

              <div className="mbp-page-numbers">
                {Array.from(
                  { length: Math.min(5, pagination.totalPages) },
                  (_, index) => {
                    let pageNumber;
                    if (pagination.totalPages <= 5) {
                      pageNumber = index + 1;
                    } else if (pagination.page <= 3) {
                      pageNumber = index + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNumber = pagination.totalPages - 4 + index;
                    } else {
                      pageNumber = pagination.page - 2 + index;
                    }

                    return (
                      <button
                        key={pageNumber}
                        type="button"
                        className={`mbp-page-btn ${pagination.page === pageNumber ? "active" : ""}`}
                        onClick={() => setCurrentPage(pageNumber)}
                        disabled={loading}
                      >
                        {pageNumber}
                      </button>
                    );
                  },
                )}
              </div>

              <button
                type="button"
                className="mbp-pagination-btn"
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.min(pagination.totalPages, page + 1),
                  )
                }
                disabled={pagination.page >= pagination.totalPages || loading}
              >
                Next <FiChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={!!selectedProduct}
        onClose={closeUpdateModal}
        maxWidth="460px"
      >
        {selectedProduct && (
          <form className="seller-stock-modal" onSubmit={saveStock}>
            <div className="seller-stock-modal__header">
              <h2>Add Stock</h2>
              <p>
                Add inventory for this product without changing the product
                catalog details.
              </p>
            </div>

            <div className="seller-stock-modal__product">
              {selectedProduct.name}
            </div>

            <div className="seller-stock-modal__field">
              <strong>
                Your Seller Stock:{" "}
                {stockNumber(selectedProduct.seller_stock_quantity)}
              </strong>
            </div>

            <label className="brand-form-label seller-stock-modal__field">
              Add Stock Quantity
              <input
                className="brand-form-input seller-stock-modal__input"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                required
                value={stockValue}
                placeholder="Enter quantity to add"
                onChange={(event) => {
                  setStockValue(event.target.value);
                  if (stockError) setStockError("");
                }}
              />
              <span className="seller-stock-modal__hint">
                Enter the quantity you want to add. This will be added to
                current stock, not replace it.
              </span>
              {stockError && (
                <span className="error-message">{stockError}</span>
              )}
            </label>
            <label className="brand-form-label seller-stock-modal__field">
              Stock Status
              <select
                className="brand-form-input seller-stock-modal__input"
                value={statusValue}
                onChange={(event) => setStatusValue(event.target.value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <div className="brand-modal-actions">
              <button
                type="button"
                className="mbp-icon-btn"
                onClick={closeUpdateModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="mbp-primary-btn"
                disabled={saving}
              >
                {saving ? "Adding..." : "Add Stock"}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={!!rejectReasonProduct}
        onClose={closeRejectReasonModal}
        maxWidth="460px"
      >
        {rejectReasonProduct && (
          <div className="seller-stock-reject-modal">
            <div className="seller-stock-reject-modal__header">
              <h2>Reject Reason</h2>
              <p>
                Review the admin feedback for this product request before
                updating and resubmitting it.
              </p>
            </div>
            <div className="seller-stock-reject-modal__details">
              <div className="seller-stock-reject-modal__row">
                <span>Product</span>
                <strong>{rejectReasonProduct.name}</strong>
              </div>
              {formatDateLabel(rejectReasonProduct.rejected_at) && (
                <div className="seller-stock-reject-modal__row">
                  <span>Rejected On</span>
                  <strong>
                    {formatDateLabel(rejectReasonProduct.rejected_at)}
                  </strong>
                </div>
              )}
            </div>
            <div className="seller-stock-reject-modal__reason">
              <span>Reason</span>
              <p>
                {rejectReasonProduct.reject_reason ||
                  "No rejection reason available."}
              </p>
            </div>
            <div className="brand-modal-actions">
              <button
                type="button"
                className="mbp-primary-btn"
                onClick={closeRejectReasonModal}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      <AddEditProductModal
        isOpen={requestModalOpen}
        onClose={closeRequestModal}
        initialData={requestModalInitialData}
        getProductRequest={handleRequestSaved}
        categorydata={categoryData}
        compainesdata={companyData}
        subcategorydata={subcategoryData}
        branddata={brandData}
        mode="request"
      />
      {actionMenu &&
        createPortal(
          <div
            ref={actionMenuRef}
            className="mbp-action-menu seller-stock-action-menu"
            style={{
              position: "fixed",
              top: actionMenu.top,
              left: actionMenu.left,
              transform: "translateX(-100%)",
            }}
          >
            {products
              .filter((product) => product.id === actionMenu.productId)
              .map((product) => (
                <React.Fragment key={product.id}>
                  {product.can_edit_request && (
                    <button
                      type="button"
                      className="mbp-action-menu-item seller-stock-action-menu-item"
                      onClick={() => {
                        setActionMenu(null);
                        openEditRequestModal(product);
                      }}
                    >
                      Edit Request
                    </button>
                  )}
                  {product.display_status === "rejected" && (
                    <button
                      type="button"
                      className="mbp-action-menu-item seller-stock-action-menu-item"
                      onClick={() => {
                        setActionMenu(null);
                        setRejectReasonProduct(product);
                      }}
                    >
                      View Reject Reason
                    </button>
                  )}
                  {product.is_editable && (
                    <button
                      type="button"
                      className="mbp-action-menu-item seller-stock-action-menu-item"
                      onClick={() => {
                        setActionMenu(null);
                        openUpdateModal(product);
                      }}
                    >
                      Add Stock
                    </button>
                  )}
                </React.Fragment>
              ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
